/* BEATSEEK — compact pop-out profile card for Wall sidebar avatars.
   Sits in the empty gutter beside the Wall with a thin connector line back to the avatar. */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, UserPlus, UserCheck, Handshake, ExternalLink, Layers, MessageCircle, Hash } from 'lucide-react';
import { HunterChip } from './HunterStats';

const API = process.env.REACT_APP_BACKEND_URL;
const CARD_W = 232;
const CARD_H = 320;

const TYPE_CHIP = {
  dj: '🎧 DJ', producer: '🎹 Producer', streamer: '📡 Streamer',
  label: '💿 Label', ar: '🎤 A&R', venue_owner: '🏛 Venue', promoter: '📣 Promoter',
  admin: '✨ Admin', mod: '🛡 Mod',
};

const typeChips = (profile) => {
  const raw = profile?.user_type;
  const list = Array.isArray(raw) ? raw : (raw ? String(raw).split(',') : []);
  const chips = list.map(t => t.trim()).filter(t => t && t !== 'regular').map(t => TYPE_CHIP[t] || t);
  if (profile?.role === 'admin' && !chips.includes(TYPE_CHIP.admin)) chips.push(TYPE_CHIP.admin);
  return chips.length ? chips : ['🎵 Music Enthusiast'];
};

const memberSince = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt) ? null : dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

// Card in the gutter left of the Wall on desktop, centred sheet on phones.
const placeCard = (rect) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const wall = document.querySelector('[data-testid="the-wall-modal"]')?.getBoundingClientRect();
  if (vw < 900) {
    return { left: clamp((vw - CARD_W) / 2, 8, vw - CARD_W - 8), top: clamp(rect.top - 20, 56, Math.max(56, vh - CARD_H - 16)), side: 'below' };
  }
  const gutterLeft = (wall ? wall.left : vw / 2) - CARD_W - 74; // clears the left rail tabs
  if (gutterLeft > 12) {
    return { left: gutterLeft, top: clamp(rect.top - 70, 16, Math.max(16, vh - CARD_H - 16)), side: 'left' };
  }
  return { left: Math.min(rect.right + 14, vw - CARD_W - 12), top: clamp(rect.top - 40, 16, Math.max(16, vh - CARD_H - 16)), side: 'right' };
};

const Connector = ({ rect, pos }) => {
  const ax = rect.left + rect.width / 2;
  const ay = rect.top + rect.height / 2;
  const r = rect.width / 2 + 6;
  let sx = ax;
  let sy = ay;
  let ex = pos.left + CARD_W;
  let ey = pos.top + 70;
  if (pos.side === 'left') { sx = ax - r; ex = pos.left + CARD_W + 2; }
  if (pos.side === 'right') { sx = ax + r; ex = pos.left - 2; }
  if (pos.side === 'below') { sy = ay + r; sx = ax; ex = pos.left + CARD_W / 2; ey = pos.top - 2; }
  return (
    <svg data-testid="wall-user-card-connector" style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex: 100016, pointerEvents: 'none' }}>
      <circle cx={ax} cy={ay} r={r} fill="none" stroke="rgba(34,211,238,0.85)" strokeWidth="1.4" />
      <line x1={sx} y1={sy} x2={ex} y2={ey} stroke="rgba(34,211,238,0.75)" strokeWidth="1.3" strokeDasharray="3 3" />
      <circle cx={ex} cy={ey} r="2.6" fill="#22d3ee" />
    </svg>
  );
};

export const WallUserCard = ({ user, rect, viewerId, mutualUsers = [], onShowPosts, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [following, setFollowing] = useState(false);
  const [friendState, setFriendState] = useState(null);
  const [msg, setMsg] = useState('');
  const [chatHint, setChatHint] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/users/${user.user_id}/mini-profile${viewerId ? `?viewer_id=${viewerId}` : ''}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) { setProfile(d); setFollowing(!!d.is_following); setFriendState(d.friend_status); } })
      .catch(() => {});
  }, [user.user_id, viewerId]);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 2200); };

  const toggleFollow = async () => {
    if (!viewerId) return flash('Log in first');
    try {
      const r = following
        ? await fetch(`${API}/api/users/${viewerId}/follow/${user.user_id}`, { method: 'DELETE' })
        : await fetch(`${API}/api/users/${viewerId}/follow`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ followed_user_id: user.user_id }),
          });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setFollowing(!following); flash(following ? 'Unfollowed' : 'Following'); }
      else if (/already following/i.test(d.detail || '')) { setFollowing(true); flash('Following'); }
      else flash('Could not follow');
    } catch { flash('Could not follow'); }
  };

  const addFriend = async () => {
    if (!viewerId) return flash('Log in first');
    if (friendState) return;
    try {
      const r = await fetch(`${API}/api/users/${viewerId}/friend-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('beatseek_token') || ''}` },
        body: JSON.stringify({ to_user_id: user.user_id }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setFriendState('pending'); flash('Request sent'); }
      else if (/already/i.test(d.detail || '')) { setFriendState(/friends/i.test(d.detail) ? 'accepted' : 'pending'); flash('Already sent'); }
      else flash('Could not send');
    } catch { flash('Could not send'); }
  };

  const message = () => {
    if (!viewerId) return flash('Log in first');
    if (friendState === 'accepted') {
      window.dispatchEvent(new CustomEvent('openWhisper', {
        detail: { user_id: user.user_id, id: user.user_id, user_name: name, name, user_avatar: avatar, avatar },
      }));
      onClose();
      return;
    }
    setChatHint(true);
  };

  const openCommunityChat = () => {
    window.dispatchEvent(new CustomEvent('openCommunityChat', { detail: { room: 'Global Community Chat' } }));
    onClose();
  };

  const pos = placeCard(rect);
  const name = profile?.username || user.username;
  const avatar = profile?.avatar || user.avatar;
  const city = [profile?.home_city, profile?.home_country].filter(Boolean).join(', ');
  const since = memberSince(profile?.member_since);

  const actionBtn = (active) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    padding: '6px 4px', borderRadius: '8px', cursor: 'pointer',
    fontSize: '10px', fontWeight: 700, letterSpacing: '0.01em', textAlign: 'center',
    background: active ? 'rgba(0,255,136,0.14)' : 'rgba(255,255,255,0.05)',
    border: `1px solid ${active ? 'rgba(0,255,136,0.5)' : 'rgba(255,255,255,0.14)'}`,
    color: active ? '#00ff88' : '#dbe4ee', transition: 'background-color 0.18s, border-color 0.18s, color 0.18s',
  });

  return createPortal(
    <>
      <div data-testid="wall-user-card-backdrop" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100015, background: 'rgba(0,0,0,0.3)' }} />
      <Connector rect={rect} pos={pos} />
      <div
        data-testid={`wall-user-card-${user.user_id}`}
        style={{
          position: 'fixed', left: `${pos.left}px`, top: `${pos.top}px`, width: `${CARD_W}px`, zIndex: 100017,
          padding: '2px', borderRadius: '14px',
          background: 'linear-gradient(90deg, #00ffff, #ff00ff, #00ffff)',
          backgroundSize: '200% 200%',
          animation: 'beatseekCardBorder 4s linear infinite',
          boxShadow: '0 0 22px rgba(0,255,255,0.3), 0 0 44px rgba(255,0,255,0.2)',
        }}
      >
        <style>{`@keyframes beatseekCardBorder { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }`}</style>
        <div style={{ position: 'relative', borderRadius: '12px', padding: '12px 11px 11px', background: 'linear-gradient(135deg, #0f172a, #1a2234, #0f172a)' }}>
          <button data-testid="wall-user-card-close" onClick={onClose} title="Close" style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', color: '#7c8ba1', cursor: 'pointer', padding: 0 }}>
            <X size={14} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            {avatar
              ? <img src={avatar} alt="" style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(0,229,255,0.5)', flexShrink: 0 }} />
              : <span style={{ width: 42, height: 42, flexShrink: 0, borderRadius: '50%', background: 'linear-gradient(135deg, #06b6d4, #c026d3)', color: '#fff', fontSize: 17, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{(name || '?').charAt(0).toUpperCase()}</span>}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '14px' }}>{name}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '3px' }}>
                {typeChips(profile).slice(0, 2).map(c => (
                  <span key={c} style={{ fontSize: '8px', fontWeight: 700, padding: '2px 6px', borderRadius: 999, color: '#c4b5fd', background: 'rgba(139,92,246,0.14)', border: '1px solid rgba(139,92,246,0.4)' }}>{c}</span>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center' }}>
            <HunterChip userId={user.user_id} />
          </div>

          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '10px', color: '#9aa8b8' }}>
            <span data-testid="wall-user-card-followers">🎧 {profile?.followers_count ?? 0} follower{(profile?.followers_count ?? 0) === 1 ? '' : 's'}{since ? ` · since ${since}` : ''}</span>
            {city && <span style={{ color: '#22d3ee', fontWeight: 600 }}>📍 {city}</span>}
            {mutualUsers.length > 0 && (
              <span style={{ color: '#00e5ff', fontWeight: 600 }}>🤝 Followed by {mutualUsers.map(m => m.username).slice(0, 2).join(', ')}{mutualUsers.length > 2 ? ` +${mutualUsers.length - 2}` : ''}</span>
            )}
          </div>

          <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
            {viewerId !== user.user_id && (
              <>
                <button data-testid={`card-follow-${user.user_id}`} onClick={toggleFollow} style={actionBtn(following)}>
                  {following ? <UserCheck size={11} /> : <UserPlus size={11} />} {following ? 'Following' : 'Follow'}
                </button>
                <button data-testid={`card-friend-${user.user_id}`} onClick={addFriend} style={actionBtn(!!friendState)}>
                  <Handshake size={11} /> {friendState === 'accepted' ? 'Friends' : friendState ? 'Requested' : 'Add friend'}
                </button>
              </>
            )}
            {viewerId !== user.user_id && (
              <button data-testid={`card-message-${user.user_id}`} onClick={message} style={actionBtn(friendState === 'accepted')}>
                <MessageCircle size={11} /> Message
              </button>
            )}
            <button data-testid={`card-posts-${user.user_id}`} onClick={() => { onShowPosts(); onClose(); }} style={actionBtn(false)}>
              <Layers size={11} /> Show posts
            </button>
            <button data-testid={`card-profile-${user.user_id}`} onClick={() => window.location.assign(`/profile/${user.user_id}`)} style={{ ...actionBtn(false), gridColumn: '1 / -1' }}>
              <ExternalLink size={11} /> Profile
            </button>
          </div>

          {chatHint && (
            <div data-testid="wall-user-card-chat-hint" style={{ marginTop: '8px', padding: '7px 8px', borderRadius: '9px', background: 'rgba(0,229,255,0.07)', border: '1px solid rgba(0,229,255,0.25)' }}>
              <div style={{ fontSize: '9.5px', lineHeight: 1.35, color: '#9fd8e6' }}>
                Direct messages are friends-only. {friendState === 'pending' ? 'Your friend request is pending.' : `Send ${name} a friend request`} — or chat openly in the community rooms.
              </div>
              <button data-testid="wall-user-card-open-chat" onClick={openCommunityChat} style={{ ...actionBtn(false), marginTop: '6px', width: '100%' }}>
                <Hash size={11} /> Open community chat
              </button>
            </div>
          )}

          {msg && <div data-testid="wall-user-card-msg" style={{ marginTop: '7px', textAlign: 'center', fontSize: '10px', fontWeight: 700, color: '#00ff88' }}>{msg}</div>}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default WallUserCard;
